import {
  Alert,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconClock,
  IconRefresh,
  IconUsersGroup,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { client } from "@/api-client";
import type {
  GroupClassSearchOutput,
  GroupMembershipOutput,
} from "@/api-types";
import { GroupClassCard } from "@/components/GroupClassCard";

interface GroupJoinRequestProps {
  group: GroupClassSearchOutput;
  groupRefetch: () => void;
  membership?: GroupMembershipOutput;
}

export const GroupJoinRequest = ({
  group,
  groupRefetch,
  membership,
}: GroupJoinRequestProps) => {
  const submitJoinRequest = client.useMutation(
    "post",
    "/owner/groups/{groupId}/join-request",
  );

  const [note, setNote] = useState("");
  const [editNote, setEditNote] = useState(membership?.note || "");

  const handleSubmitRequest = async () => {
    try {
      await submitJoinRequest.mutateAsync({
        params: { path: { groupId: group.group.id } },
        body: { note: note || undefined },
      });
      setNote("");
      await groupRefetch();
    } catch (error) {
      console.error("Failed to submit join request:", error);
      // You could add a toast notification here if you have a notification system
    }
  };

  const handleTryAgain = async () => {
    try {
      await submitJoinRequest.mutateAsync({
        params: { path: { groupId: group.group.id } },
        body: { note: editNote || undefined },
      });
      await groupRefetch();
    } catch (error) {
      console.error("Failed to resubmit request:", error);
      // You could add a toast notification here if you have a notification system
    }
  };

  const renderJoinRequestSection = () => {
    if (membership) {
      const isRejected = membership.status === "REJECTED";

      return (
        <>
          <Group justify="space-between">
            <Group gap="xs">
              {isRejected ? (
                <IconX size={20} color="red" />
              ) : (
                <IconClock size={20} color="orange" />
              )}
              <Text fw={500}>{isRejected ? "Rejected" : "Pending"}</Text>
            </Group>
          </Group>
          <Text size="sm">
            {isRejected
              ? "Your request to join this group was rejected by the trainer."
              : "Your request to join this group is pending approval from the trainer."}
          </Text>
          {membership.note && (
            <Paper p="sm" bg="gray.0">
              <Text size="sm" fw={500} mb="xs">
                Your message:
              </Text>
              <Text size="sm">{membership.note}</Text>
            </Paper>
          )}
          {isRejected && (
            <Stack gap="sm">
              <Textarea
                label="New nessage (Optional)"
                placeholder="Tell the trainer why you'd like to join this group..."
                rows={3}
                value={editNote}
                onChange={(event) => setEditNote(event.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={handleTryAgain}
                  loading={submitJoinRequest.isPending}
                >
                  Try Again
                </Button>
              </Group>
            </Stack>
          )}
        </>
      );
    }

    // Always allow join requests since groups in this context are available
    return (
      <Stack gap="sm">
        <Group gap="xs">
          <IconUsersGroup size={20} color="blue" />
          <Text fw={500}>Request to join this group</Text>
        </Group>
        <Text size="sm">
          Click to request to join this group. You will be notified when the
          trainer approves or rejects your request.
        </Text>
        {group.group.spotsLeft === 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="orange"
            variant="light"
          >
            This group seems to be full, but you can still ask to join.
          </Alert>
        )}
        <Textarea
          label="Message (Optional)"
          placeholder="Tell the trainer why you'd like to join this group..."
          rows={3}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button
            w="100%"
            onClick={handleSubmitRequest}
            loading={submitJoinRequest.isPending}
          >
            Submit Request
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Stack gap="md">
      {/* Group Info */}
      <GroupClassCard groupClass={group} showJoinButton={false} />

      {/* Join Request Section */}
      <Paper p="md" withBorder>
        <Stack gap="sm">{renderJoinRequestSection()}</Stack>
      </Paper>
    </Stack>
  );
};
